import type { CityConfig } from "../city-template/types";

export const berlinConfig: CityConfig = {
  city: "Berlin",
  state: "New Hampshire",
  stateAbbr: "NH",
  slug: "berlin",
  route: "/cities/berlin",
  title: "Emergency Passport Berlin, NH | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Berlin, NH — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Coos County and North Country New Hampshire travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Berlin, NH",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Berlin and North Country New Hampshire travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Berlin%20New%20Hampshire%20scenic%20view%20of%20the%20historic%20North%20Country%20city%20with%20the%20Androscoggin%20River%20flowing%20through%20the%20valley%2C%20the%20dramatic%20White%20Mountains%20in%20the%20background%2C%20charming%20New%20England%20industrial%20heritage%20buildings%20with%20red%20brick%20facades%2C%20warm%20autumn%20afternoon%20light%20with%20golden%20and%20orange%20foliage%2C%20clean%20and%20inviting%20rural%20mountain%20town%20scene%2C%20no%20visible%20text%20or%20people%2C%20professional%20landscape%20photography%2C%20warm%20earth%20tones%20and%20soft%20blue%20sky%2C%20photorealistic%2C%20New%20England%20North%20Country%20charm&width=1800&height=700&seq=berlin-nh-hero-001&orientation=landscape",
  heroImageAlt: "Berlin New Hampshire scenic view with the Androscoggin River and White Mountains in the background",
  geoPosition: "44.4688;-71.1847",
  geoPlacename: "Berlin, New Hampshire",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Customs House St, Boston, MA 02103",
    distance: "Located in downtown Boston — approximately 3.5 to 4 hours from Berlin via I-93 and Route 3",
  },
  nearbyCitiesZips: [
    { name: "Berlin", zips: "03570" },
    { name: "Gorham", zips: "03581" },
    { name: "Milan", zips: "03588" },
    { name: "Lancaster", zips: "03584" },
    { name: "Whitefield", zips: "03598" },
    { name: "Colebrook", zips: "03576" },
    { name: "Stewartstown", zips: "03576" },
    { name: "Pittsburg", zips: "03592" },
    { name: "Dummer", zips: "03588" },
    { name: "Success", zips: "03570" },
    { name: "Randolph", zips: "03593" },
    { name: "Jefferson", zips: "03583" },
    { name: "Carroll", zips: "03595" },
    { name: "Bethlehem", zips: "03574" },
    { name: "Littleton", zips: "03561" },
    { name: "Lisbon", zips: "03585" },
    { name: "Northumberland", zips: "03582" },
    { name: "Groveton", zips: "03582" },
    { name: "Errol", zips: "03579" },
    { name: "Dixville", zips: "03576" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent North Country New Hampshire departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Manchester", route: "/cities/manchester" },
    { name: "Nashua", route: "/cities/nashua" },
    { name: "Concord", route: "/cities/concord-nh" },
    { name: "Laconia", route: "/cities/laconia" },
    { name: "Lebanon", route: "/cities/lebanon" },
    { name: "Lancaster", route: "/cities/lancaster" },
    { name: "Littleton", route: "/littleton" },
    { name: "Plymouth", route: "/cities/plymouth" },
    { name: "Hanover", route: "/cities/hanover" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Berlin remotely via secure courier. We do not have a walk-in office in Berlin — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for North Country New Hampshire travelers.",
  fpcOfficeDistance: "Serving all of Coos County and northern New Hampshire",
  serviceAreaIntro: "From Berlin's historic downtown Main Street to the Glen neighborhood, from the Androscoggin River valley to the White Mountains foothills — Fast Passport Center serves the entire Berlin community. We regularly help travelers from Gorham, Milan, Lancaster, and Whitefield get their passports fast.",
  nearbyCitiesIntro: "We serve all Berlin neighborhoods and nearby North Country cities including Gorham, Milan, Lancaster, Whitefield, Colebrook, Stewartstown, Pittsburg, Dummer, Randolph, Jefferson, Carroll, Bethlehem, Littleton, Lisbon, Northumberland, Groveton, Errol, and Dixville. If you are in the greater Berlin area, we have you covered.",
  howItWorksIntro: "Most Berlin travelers complete everything online and ship documents overnight. No need to drive to Boston or Lebanon — our courier handles the in-person delivery for you.",
  mistakesIntro: "With remote North Country location and limited local passport services, Berlin travelers especially can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Berlin and North Country New Hampshire travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Berlin? Here are the most common ones we hear from North Country New Hampshire travelers.",
  statePageRoute: "/states/new-hampshire",
  statePageName: "New Hampshire",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Berlin international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Margaret L.",
      location: "Berlin, NH",
      rating: 5,
      text: "My trip to visit family in Ireland was in 5 days and I discovered my passport was expired. Living in the North Country, I thought I was out of options. Fast Passport Center saved my trip with 48-hour service.",
      service: "Passport Renewal",
    },
    {
      name: "William D.",
      location: "Gorham, NH",
      rating: 5,
      text: "Needed an emergency passport for a medical conference in Toronto. They understood how remote we are and made sure everything went smoothly. Had it in 3 days.",
      service: "Emergency Passport",
    },
    {
      name: "Elizabeth A.",
      location: "Whitefield, NH",
      rating: 5,
      text: "Lost my passport before a cruise to the Caribbean. I was in a panic because the closest passport office is so far away. Fast Passport Center made it simple and I got my replacement in 2 days.",
      service: "Lost Passport",
    },
    {
      name: "Richard M.",
      location: "Lancaster, NH",
      rating: 5,
      text: "As a frequent traveler from the North Country, I rely on Fast Passport Center. They understand the challenges of living in a remote area and always deliver. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "berlin-nh-1",
      name: "Berlin Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "15 Pleasant St",
      city: "Berlin",
      state: "NH",
      zip: "03570",
      phone: "(603) 752-1600",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Berlin",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Berlin. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "berlin-nh-2",
      name: "Gorham Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1 Main St",
      city: "Gorham",
      state: "NH",
      zip: "03581",
      phone: "(603) 466-2271",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.1 miles from downtown Gorham",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southern Coos County residents. Limited hours — book appointments well in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "berlin-nh-3",
      name: "Lancaster Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "30 Main St",
      city: "Lancaster",
      state: "NH",
      zip: "03584",
      phone: "(603) 788-4455",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.3 miles from downtown Lancaster",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for northern Coos County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "berlin-nh-4",
      name: "Whitefield Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "20 Jefferson Rd",
      city: "Whitefield",
      state: "NH",
      zip: "03598",
      phone: "(603) 837-2530",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.2 miles from downtown Whitefield",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for western Coos County residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "berlin-nh-5",
      name: "Colebrook Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "14 Main St",
      city: "Colebrook",
      state: "NH",
      zip: "03576",
      phone: "(603) 237-5210",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.1 miles from downtown Colebrook",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves northern Coos County near the Vermont border. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "berlin-nh-6",
      name: "Littleton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "134 Main St",
      city: "Littleton",
      state: "NH",
      zip: "03561",
      phone: "(603) 444-6881",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Littleton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves southern Coos County near the White Mountains. Extended hours. Appointments recommended.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Berlin photo shops often use generic sizes. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Coos County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Berlin applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Berlin families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Berlin travelers often assume standard processing will be fine. It takes 6–13 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Berlin." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Berlin address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Berlin before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Berlin for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most North Country New Hampshire travelers." },
    { q: "How fast can I renew my passport in Berlin?", a: "Fast Passport Center offers fast passport renewal in Berlin in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Berlin in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Berlin?", a: "Same-day passport appointments are only available at the Boston Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Berlin residents." },
    { q: "Can I expedite a child passport in Berlin?", a: "Yes. Fast Passport Center expedites child passports in Berlin for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Berlin with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Berlin?", a: "With Fast Passport Center's emergency courier service, Berlin travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to visit the Boston Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Berlin?", a: "Popular options include the Berlin Post Office (03570), Gorham Post Office (03581), Lancaster Post Office (03584), Whitefield Post Office (03598), Colebrook Post Office (03576), and Littleton Post Office (03561). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Berlin?", a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option." },
    { q: "What if I live in a nearby town like Gorham or Lancaster?", a: "We serve all of Coos County and northern New Hampshire including Gorham, Milan, Lancaster, Whitefield, Colebrook, Stewartstown, Pittsburg, Dummer, Randolph, Jefferson, Carroll, Bethlehem, Littleton, Lisbon, Northumberland, Groveton, Errol, and Dixville. Our overnight shipping labels work from any UPS location, so your remote location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Berlin?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Berlin", address: "65 Pleasant St, Berlin, NH 03570", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Gorham", address: "10 Main St, Gorham, NH 03581", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Rite Aid — Lancaster", address: "35 Main St, Lancaster, NH 03584", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Littleton", address: "615 Meadow St, Littleton, NH 03561", hours: "Mon–Sun: 6AM–11PM" },
    { name: "UPS Store — Littleton", address: "134 Main St, Littleton, NH 03561", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Shaw's — Berlin", address: "55 Pleasant St, Berlin, NH 03570", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Hannaford — Littleton", address: "10 Maple St, Littleton, NH 03561", hours: "Mon–Sun: 7AM–10PM" },
    { name: "Dollar General — Gorham", address: "8 Main St, Gorham, NH 03581", hours: "Mon–Sun: 8AM–10PM" },
  ],
  dmvLocations: [
    { name: "DMV — Berlin", address: "15 Pleasant St, Berlin, NH 03570", hours: "Mon–Fri: 8AM–5PM", note: "Full-service center for Coos County residents. Appointment recommended.", featured: true },
    { name: "DMV — Gorham", address: "1 Main St, Gorham, NH 03581", hours: "Mon–Fri: 8AM–5PM", note: "Passport-ready photo services available. Online appointment booking.", featured: false },
    { name: "DMV — Littleton", address: "134 Main St, Littleton, NH 03561", hours: "Mon–Fri: 8AM–5PM", note: "REAL ID processing. Walk-ins limited — book online first.", featured: false },
    { name: "DMV — Lancaster", address: "30 Main St, Lancaster, NH 03584", hours: "Mon–Fri: 8AM–5PM", note: "Full-service DMV for northern Coos County residents.", featured: true },
  ],
  airports: [
    { code: "MHT", name: "Manchester-Boston Regional Airport", distance: "120 miles from Berlin", address: "1 Airport Rd, Manchester, NH 03103", note: "Closest major commercial airport for Berlin travelers. International flights to Canada and Europe.", flightUrl: "https://www.flymanchester.com/", flightLabel: "MHT Flight Info", featured: true },
    { code: "LEB", name: "Lebanon Municipal Airport", distance: "95 miles from Berlin", address: "5 Airpark Rd, West Lebanon, NH 03784", note: "Regional airport with commercial flights to Boston and New York. Accessible via I-91.", flightUrl: "https://www.lebanonairport.com/", flightLabel: "Lebanon Airport", featured: false },
    { code: "BOS", name: "Boston Logan International Airport", distance: "190 miles from Berlin", address: "1 Harborside Dr, Boston, MA 02128", note: "Major international hub for long-haul flights. Accessible via I-93 and Route 3.", flightUrl: "https://www.massport.com/logan-airport", flightLabel: "Logan Airport", featured: false },
  ],
};